import type * as React from "react";
import type { LucideIcon } from "lucide-react";
import { Card } from "@hp/ui";

export interface StatTileProps {
  label: string;
  value: string;
  sublabel?: string;
  icon: LucideIcon;
  tone?: "neutral" | "brand" | "good" | "warning" | "critical";
  /** When set, the whole tile is a button that runs this on click / Enter / Space. */
  onClick?: () => void;
  /** Small call-to-action line shown under the sublabel on a clickable tile. */
  actionLabel?: string;
}

const TONE_VALUE_CLASSES: Record<NonNullable<StatTileProps["tone"]>, string> = {
  neutral: "text-zinc-900",
  brand: "text-[var(--color-accent-content)]",
  good: "text-emerald-700",
  warning: "text-amber-700",
  critical: "text-red-700",
};

const TONE_CHIP_CLASSES: Record<NonNullable<StatTileProps["tone"]>, string> = {
  neutral: "bg-zinc-400/15 text-zinc-600",
  brand: "bg-amber-400/20 text-amber-800",
  good: "bg-emerald-400/20 text-emerald-800",
  warning: "bg-amber-400/25 text-amber-800",
  critical: "bg-red-400/20 text-red-700",
};

/** Flux kanban-card style KPI tile. */
export function StatTile({ label, value, sublabel, icon: Icon, tone = "neutral", onClick, actionLabel }: StatTileProps) {
  const interactive = onClick
    ? {
        role: "button" as const,
        tabIndex: 0,
        onClick,
        onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onClick();
          }
        },
      }
    : {};

  return (
    <Card
      className={[
        "rounded-lg p-0 transition-shadow hover:shadow-sm",
        onClick
          ? "cursor-pointer hover:border-zinc-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-300"
          : "",
      ].join(" ")}
      {...interactive}
    >
      <div className="flex items-start justify-between gap-3 px-4 py-3 sm:gap-4 sm:px-5 sm:py-4">
        <div className="min-w-0">
          <div className="text-xs font-medium uppercase tracking-wide text-zinc-500">{label}</div>
          <div
            className={[
              "mt-1.5 break-words font-mono text-xl font-semibold leading-tight tabular-nums sm:text-2xl",
              TONE_VALUE_CLASSES[tone],
            ].join(" ")}
          >
            {value}
          </div>
          {sublabel ? <div className="mt-1 text-xs leading-snug text-zinc-500">{sublabel}</div> : null}
          {onClick && actionLabel ? (
            <div className="mt-2 text-xs font-medium text-[var(--color-accent-content)]">{actionLabel} ↗</div>
          ) : null}
        </div>
        <div
          className={[
            "flex h-10 w-10 flex-none items-center justify-center rounded-lg",
            TONE_CHIP_CLASSES[tone],
          ].join(" ")}
        >
          <Icon className="h-5 w-5" aria-hidden />
        </div>
      </div>
    </Card>
  );
}
